import styled from 'styled-components/macro'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'
import { Layout, Text } from '@loadsmart/miranda-react'

import { screen } from 'legacy/styles/screen'

import { type DistanceUnit } from 'rfp/domain/DistanceUnit'

import { type Stop, getDelivery, getExtraStops, getPickup, isMultiStop } from '../../../domain/Stop'
import { formatLaneDistance } from '../../../presenters/stopPresenter'
import { Information } from '../information/Information'
import { ExtraStopInformation } from './ExtraStopInformation'

const Divider = styled.div`
  background: ${getToken('color-neutral-light')};
  height: ${getToken('space-2xs')};
  width: ${getToken('space-xl')};
  display: none;

  ${screen.md} {
    display: block;
  }
`

const LaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: ${getToken('space-m')};

  ${screen.md} {
    align-items: center;
    flex-direction: row;
  }
`

export type LaneInformationProps = {
  distance?: number
  stops: Array<Stop>
  valueFormatter: (stop: Stop) => string
  extraFormatter?: (stop: Stop) => string | undefined
  distanceUnit: DistanceUnit
}

export function LaneInformation({
  distance,
  stops,
  valueFormatter,
  extraFormatter,
  distanceUnit,
  ...props
}: LaneInformationProps) {
  if (stops.length < 2) {
    return null
  }

  const hasDistance = Boolean(distance)
  const hasExtraStops = isMultiStop(stops)
  const shouldDisplayExtraInformation = hasDistance || hasExtraStops

  const pickup = getPickup(stops)
  const delivery = getDelivery(stops)

  return (
    <LaneWrapper {...props}>
      <Information title={valueFormatter(pickup)} subTitle={extraFormatter?.(pickup)} />
      <Divider />
      {shouldDisplayExtraInformation && (
        <>
          <Layout.Stack align="center" gap="spacing-1">
            {hasDistance && (
              <Text variant="body-md-bold">{`${formatLaneDistance(distance, distanceUnit)}`}</Text>
            )}
            {hasExtraStops && (
              <ExtraStopInformation
                stops={getExtraStops(stops)}
                titleFormatter={valueFormatter}
                subTitleFormatter={extraFormatter}
              />
            )}
          </Layout.Stack>
          <Divider />
        </>
      )}
      <Information title={valueFormatter(delivery)} subTitle={extraFormatter?.(delivery)} />
    </LaneWrapper>
  )
}
